import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import cn from 'classnames';

import RightSidePanelSection from '@/components/common/RightSidePanel/RightSidePanelSection';
import SortingButton from './components/SortingButton/SortingButton';
import NoteItem from './components/NoteItem/NoteItem';
import ButtonLink from '@/components/common/ButtonLink/ButtonLink';
import AddNoteModal from './components/AddNoteModal/AddNoteModal';
import { useGetContractNotes } from './hooks/useGetContractNotes';
import { OrderType } from '@/graphql/contracts/_generated_/contracts';
import { useAddContractNote } from './hooks/useAddContractNote';
import NoteItemSkeleton from './components/NoteItem/NoteItemSkeleton';
import { useIntersectionObserver } from '../../../../hooks/useIntersectionObserver';
import { useEffectExceptOnMount } from '@/hooks/useEffectExceptOnMount.hook';
import { useSearchUsers } from '@/hooks/identity/useSearchUsers';
import { User } from '@/hooks/identity/responseModels/user';

interface ListMetadata {
  pageNumber: number;
  order: OrderType;
}

export default function NotesContainer() {
  const { contractId }: { contractId: UUID } = useParams();
  const [isVisibleNewNoteModal, setIsVisibleNewNoteModal] = useState(false);
  const { getContractNotes, notesData, isLoading, isCalled } = useGetContractNotes();
  const { searchUsers, isLoadingSearchUsers } = useSearchUsers();
  const [users, setUsers] = useState<User[]>([]);
  const { addNote } = useAddContractNote();
  const ref = useRef<HTMLDivElement>(null);
  const entry = useIntersectionObserver(ref, { threshold: 0.5 });
  const isVisibleEntry = !!entry?.isIntersecting;
  const [notes, setNotes] = useState<ContractNote[]>([]);

  const [listMetadata, setListMetadata] = useState<ListMetadata>({
    pageNumber: 0,
    order: OrderType.Descending
  });

  const isVisibleEmptyMessage = isCalled && !isLoading && notes.length === 0;
  const isVisibleScrollSkeleton = !isVisibleEmptyMessage
    && !Boolean(notesData?.totalPages && listMetadata.pageNumber >= notesData?.totalPages);

  useEffect(() => {
    if (isVisibleEntry && !isLoading) {
      setListMetadata((prevState) => ({
        ...prevState,
        pageNumber: prevState.pageNumber + 1,
      }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisibleEntry]);

  useEffectExceptOnMount(() => {
    if (notesData?.data && !isLoading) {
      const getUsers = async () => {
        const userIds = notesData.data.map(note => note.userID);
        const uniqueUserIds = new Set(userIds);
        const searchResult = await searchUsers({ ids: [...uniqueUserIds] });
        setUsers(searchResult);
      };

      getUsers().then(() => {
        setNotes(() => [
          ...notes,
          ...notesData.data
        ]);
      });
    }
  }, [notesData]);

  useEffectExceptOnMount(() => {
    getContractNotes({ contractId, ...listMetadata });
  }, [listMetadata]);

  const skeleton = useMemo(() => {
    if (notes.length === 0) {
      return Array.from({ length: 3 }).map((_, index) => (
        <NoteItemSkeleton key={index} className="mb-25" />
      ));
    }

    return <NoteItemSkeleton />;
  }, [notes]);

  const onSaveNote = (text: string) => {
    addNote({ contractId, text }, () => {
      setIsVisibleNewNoteModal(false);
      setNotes([]);
      setListMetadata((prevState) => ({
        order: prevState.order,
        pageNumber: 1
      }));
    });
  };

  const onSort = (newOrder: OrderType) => {
    setNotes([]);
    setListMetadata({
      order: newOrder,
      pageNumber: 1
    });
  };

  return (
    <RightSidePanelSection title="Notes">
      <div className="mt-20" />

      <div className="mb-20 d-flex justify-content-between">
        <SortingButton disabled={isLoading} onChange={onSort} />

        <ButtonLink onClick={() => setIsVisibleNewNoteModal(true)}>+ Add Note</ButtonLink>
      </div>

      {isVisibleNewNoteModal && (
        <AddNoteModal
          onClose={() => setIsVisibleNewNoteModal(false)}
          onSave={onSaveNote}
        />
      )}

      <div>
        {notes.map((el, index) => (
          <NoteItem
            key={el.contractNoteID}
            className={cn({ 'mb-25': index <= notes.length - 1 })}
            contractNote={el}
            user={users.find(user => user.Id == el.userID)!}
            isLoading={isLoadingSearchUsers}
          />
        ))}

        {isVisibleEmptyMessage && (
          <div className="mt-30 mb-30 d-flex justify-content-center">
            There are no any notes yet in this contract
          </div>
        )}

        {isVisibleScrollSkeleton && (
          <div ref={ref}>
            {skeleton}
          </div>
        )}
      </div>
    </RightSidePanelSection>
  );
}
